import { useEffect, useState } from "react";
import { API } from "../lib/api";
import { ChevronLeft, ChevronRight, Images, X, Link2, Link2Off } from "lucide-react";

export default function ChapterSteps({ chapter, videoRef }) {
  const steps = chapter.steps || [];
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  const [synced, setSynced] = useState(true);
  useEffect(() => { setI(0); setOpen(false); }, [chapter.slug]);

  useEffect(() => {
    if (!synced || !steps.length) return;
    const id = setInterval(() => {
      const v = videoRef?.current;
      if (!v || v.paused) return;
      const t = v.currentTime;
      let k = 0;
      for (let s = 0; s < steps.length; s++) if (t >= (steps[s].at || 0)) k = s;
      setI((cur) => (cur === k ? cur : k));
    }, 400);
    return () => clearInterval(id);
  }, [videoRef, synced, steps, chapter.slug]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") setOpen(false); if (e.key === "ArrowRight") pick(Math.min(steps.length - 1, i + 1)); if (e.key === "ArrowLeft") pick(Math.max(0, i - 1)); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  });

  if (!steps.length) return null;
  const src = (s) => `${API}/learn/media/${s.image}`;
  const cur = steps[i];
  const pick = (k) => { setI(k); const v = videoRef?.current; if (synced && v && Number.isFinite(steps[k].at)) { v.currentTime = steps[k].at; } };
  const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

  return (
    <section className="p-5 rounded-2xl bg-[#131722] border border-slate-800 space-y-4" data-testid="chapter-steps">
      <div className="flex items-center gap-3">
        <img src="/raq-avatar.png" alt="" className="w-8 h-8 rounded-full" />
        <div className="flex-1 min-w-0"><p className="font-mono text-xs tracking-widest uppercase text-cyan-400">// Raq shows you · step by step</p><h3 className="font-display text-lg font-bold">{steps.length} screens in this chapter</h3></div>
        <button onClick={() => setSynced((v) => !v)} data-testid="chapter-steps-sync" title={synced ? "Images follow the video. Click to browse freely." : "Click to sync images with the video"}
          className={`h-8 px-2.5 rounded-full border text-xs font-mono flex items-center gap-1.5 ${synced ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-400"}`}>
          {synced ? <Link2 className="w-3.5 h-3.5" /> : <Link2Off className="w-3.5 h-3.5" />}{synced ? "Synced to video" : "Free browse"}
        </button>
        <Images className="w-4 h-4 text-slate-500 hidden sm:block" />
      </div>
      <button onClick={() => setOpen(true)} className="w-full text-left group" data-testid="chapter-step-main">
        <div className="relative rounded-xl overflow-hidden border border-slate-800">
          <img key={cur.image} src={src(cur)} alt={cur.caption} className="w-full aspect-video object-cover object-top rise" data-testid="chapter-step-image" />
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/80 to-transparent">
            <p className="text-sm text-slate-100"><span className="font-mono text-cyan-400 mr-2">Step {cur.n}</span>{cur.caption}<span className="font-mono text-slate-500 text-xs ml-2">{fmt(cur.at || 0)}</span></p>
          </div>
        </div>
      </button>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="chapter-step-strip">
        {steps.map((s, k) => (
          <button key={s.image} onClick={() => pick(k)} data-testid={`chapter-step-${s.n}`} title={s.caption}
            className={`relative shrink-0 w-28 aspect-video rounded-md overflow-hidden border transition-[border-color,opacity] ${k === i ? "border-cyan-400 opacity-100" : "border-slate-800 opacity-60 hover:opacity-100"}`}>
            <img src={src(s)} alt="" loading="lazy" className="w-full h-full object-cover object-top" />
            <span className="absolute left-1 top-1 text-[10px] font-mono bg-[#0B0D12]/80 text-cyan-300 px-1 rounded">{s.n}</span>
            <span className="absolute right-1 bottom-1 text-[10px] font-mono bg-[#0B0D12]/80 text-slate-300 px-1 rounded">{fmt(s.at || 0)}</span>
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0B0D12]/95 backdrop-blur-sm flex flex-col" data-testid="chapter-step-lightbox" onClick={() => setOpen(false)}>
          <div className="flex items-center justify-between px-5 py-3 text-sm text-slate-300"><span className="font-mono text-cyan-400">Step {cur.n} of {steps.length}</span><button onClick={() => setOpen(false)} aria-label="Close" data-testid="chapter-step-close"><X className="w-5 h-5" /></button></div>
          <div className="flex-1 flex items-center justify-center gap-3 px-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => pick(Math.max(0, i - 1))} disabled={i === 0} className="p-2 rounded-full border border-slate-700 disabled:opacity-30" data-testid="chapter-step-prev"><ChevronLeft className="w-5 h-5" /></button>
            <img src={src(cur)} alt={cur.caption} className="max-h-[75vh] max-w-[85vw] rounded-xl border border-slate-800 object-contain" />
            <button onClick={() => pick(Math.min(steps.length - 1, i + 1))} disabled={i === steps.length - 1} className="p-2 rounded-full border border-slate-700 disabled:opacity-30" data-testid="chapter-step-next"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <p className="text-center text-slate-200 px-6 py-4">{cur.caption}</p>
        </div>
      )}
    </section>
  );
}
